import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AccidentFormValues, AccidentTypeSchema, mapAccidentForFrontend, accidentFormSchema, getDamageSectionTitle, getSectionOneTitle, mapAccidentForBackend, InjuryType, INJURY_TYPE_SECTIONS, INJURY_SEVERITIES } from "./types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, DateTimeInput, Form, FormField, FormInput, FormLabel, FormMessage, Textarea } from "../ui";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View } from "react-native";
import { Text } from "../ui/text";
import { trpc } from "@/trpc/react";
import { useRouter } from "expo-router";
import { JobDatesCrewList } from "../types/JobDateCrewList";
import Toast from "react-native-toast-message";
import FileInput, { FileProps } from "../file-input";
import { supabase } from "@/lib/supabase";
import { decode } from "base64-arraybuffer";
import { CrewList } from "../types/CrewList";
import React from "react";
import { DocumentType } from "@crew-api/server/schema/document.schema";
import { Ionicons } from "@expo/vector-icons";

interface AccidentFormProps {
  jobDate: JobDatesCrewList | undefined | null;
  crewList: CrewList | undefined;
}


export default function AccidentForm({ jobDate, crewList }: AccidentFormProps) {
  const router = useRouter();

  const insets = useSafeAreaInsets();
  const contentInsets = {
    top: insets.top,
    bottom: insets.bottom,
    left: 12,
    right: 12,
  };

  const form = useForm<AccidentFormValues>({
    resolver: zodResolver(accidentFormSchema),
    defaultValues: mapAccidentForFrontend(jobDate),
  });

  const [selectedFiles, setSelectedFiles] = React.useState<FileProps[]>([]);
  const [photoError, setPhotoError] = React.useState<string>('');

  const { mutate: addDocument } = trpc.document.addDocument.useMutation();

  const { mutate: createAccident, isPending: uploadLoading } = trpc.accident.create.useMutation({
    onSuccess: async (response, variables) => {
      if (selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const { error: uploadError } = await supabase.storage
            .from("jobs")
            .upload(`${variables.job_id}/accidents/${response.id}/${file.name}`, decode(file.base64EncodedFile), { contentType: file.type, upsert: true });

          addDocument({
            job_id: variables.job_id,
            crew_id: variables.crew_id,
            file_name: file.name || '',
            file_type: file.type,
            file_size: file.base64EncodedFile.length * 0.75,
            file_blob_location: `${variables.job_id}/accidents/${response.id}/${file.name}`,
            type: DocumentType.Accident,
          })

          if (uploadError) {
            Toast.show({
              type: 'error',
              text1: `Failed to save file ${file.name}`
            });
          }
        }
      }

      router.back();
      Toast.show({
        type: 'success',
        text1: 'Accident successfully submitted!'
      });

      createTask({
        title: `New Accident report for job #${variables?.job_id} and crew #${variables?.crew_id}`,
        description: `New Accident report for job #${variables.job_id} and crew #${variables.crew_id}`,
        priority: 1,
        tags: ["accident"],
        due_date: new Date(),
        crew_id: variables.crew_id,
        job_id: variables.job_id,
        state_name: "Open",
        type: "accident.create",
        team_id: 2,
        accident_id: response.id
      })
    },
  });

  const { mutate: createTask, isPending: isCreatingTask } = trpc.task.createTask.useMutation();

  async function onSubmit(data: AccidentFormValues) {
    const jobId = jobDate?.job_id;
    const jobDateId = jobDate?.date_id;

    if (!jobId || !jobDateId) {
      return;
    }

    // Check if at least one photo is uploaded
    if (selectedFiles.length === 0) {
      setPhotoError('At least one photo is required');
      Toast.show({
        type: 'error',
        text1: 'Photo Required',
        text2: 'Please upload at least one photo of the accident before submitting.'
      });
      return;
    }

    createAccident(mapAccidentForBackend({ ...data, job_date_id: jobDateId, job_id: jobId }));
  };

  function onFileSelect(files: FileProps[] | FileProps) {
    if (Array.isArray(files)) {
      setSelectedFiles(files);
    } else {
      setSelectedFiles([files]);
    }
    // Clear photo error when files are selected
    if (photoError) {
      setPhotoError('');
    }
  }

  const selectedType = form.watch('type');
  const isDamageType = selectedType === 'Equipment Damage' || selectedType === 'Venue Damage';
  const isInjuryType = selectedType === 'Injury' || selectedType === 'Near Miss';

  const selectedCrewId = form.watch('crew_id');

  const { data: vehiclesData } = trpc.vehicle.getVehiclesForJobDate.useQuery(jobDate?.date_id!, { enabled: Boolean(jobDate) })

  const { fields: witnessFields, append: addWitness, remove: removeWitness } = useFieldArray({
    control: form.control,
    name: 'witnesses',
  });

  const { fields: vehicleFields, append: addVehicle, remove: removeVehicle } = useFieldArray({
    control: form.control,
    name: 'vehicles'
  })

  return (
    <Form {...form}>
      <Card className='mt-4 mb-4'>
        <CardHeader>
          <CardTitle>What kind of accident / incident?</CardTitle>
        </CardHeader>
        <CardContent>
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <Select onValueChange={(option) => field.onChange(option?.value || '')}>
                <SelectTrigger className='w-[250px]'>
                  <SelectValue
                    className='text-foreground text-sm native:text-lg'
                    placeholder='Select a type'
                  />
                </SelectTrigger>
                <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                  <SelectGroup>
                    {AccidentTypeSchema.options.map((type, index) => (
                      <SelectItem key={index} label={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
          />
        </CardContent>
      </Card>
      {selectedType && (
        <>
          <Card className='mt-4 mb-4'>
            <CardHeader>
              <CardTitle>{getSectionOneTitle(selectedType)}</CardTitle>
            </CardHeader>
            <CardContent>
              <View className="gap-4">
                {(isInjuryType || selectedType === 'Incident' || selectedType === 'Vehicle Accident or Damage') && (
                  <>
                    <FormField
                      control={form.control}
                      name="crew_id"
                      render={({ field }) => (
                        <Select onValueChange={(option) => field.onChange(option?.value || '')}>
                          <SelectTrigger className='w-[250px]'>
                            <SelectValue
                              className='text-foreground text-sm native:text-lg'
                              placeholder='Select a person'
                            />
                          </SelectTrigger>
                          <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                            <SelectGroup>
                              {crewList?.map((crewItem, index) => (
                                <SelectItem key={index} label={crewItem.crew?.name || ''} value={crewItem.crew_id?.toString() || ''}>
                                  {crewItem.crew?.name || ''}
                                </SelectItem>
                              ))}
                              <SelectItem label="Other" value="Other">
                                Other
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {selectedCrewId === 'Other' && (
                      <View className="gap-4">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormInput
                              label="Name"
                              {...field}
                            />
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="telephone"
                          render={({ field }) => (
                            <FormInput
                              label="Telephone"
                              {...field}
                            />
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormInput
                              label="Email"
                              {...field}
                            />
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="position"
                          render={({ field }) => (
                            <FormInput
                              label="Position"
                              {...field}
                            />
                          )}
                        />
                        <FormLabel>Address</FormLabel>
                        <FormField
                          control={form.control}
                          name="address"
                          render={({ field }) => (
                            <Textarea
                              {...field}
                              onChangeText={(value) => {
                                field.onChange(value);
                              }}
                              value={field.value || ''}
                            />
                          )}
                        />
                      </View>
                    )}
                  </>
                )}
                {isDamageType && (
                  <>
                    <FormLabel>Item(s)</FormLabel>
                    <FormField
                      control={form.control}
                      name="damage_item"
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          onChangeText={(value) => {
                            field.onChange(value);
                          }}
                          value={field.value || ''}
                        />
                      )}
                    />
                    {selectedType === 'Equipment Damage' && (
                      <FormField
                        control={form.control}
                        name="damage_serial"
                        render={({ field }) => (
                          <FormInput
                            label="Serial Number (if applicable)"
                            {...field}
                          />
                        )}
                      />
                    )}
                  </>
                )}
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <DateTimeInput
                      {...field}
                      label="Date"
                      value={field.value || null}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name="time"
                  render={({ field }) => (
                    <FormInput
                      label="Time"
                      {...field}
                    />
                  )}
                />
                <FormField
                  control={form.control}
                  name="place"
                  render={({ field }) => (
                    <FormInput
                      label="Location"
                      {...field}
                    />
                  )}
                />
              </View>
            </CardContent>
          </Card>
          {isDamageType && (
            <>
              <Card className='mt-4 mb-4'>
                <CardHeader>
                  <CardTitle>Who's {getDamageSectionTitle(selectedType)} got damaged?</CardTitle>
                </CardHeader>
                <CardContent>
                  <View className="gap-4">
                    <FormField
                      control={form.control}
                      name="damage_name"
                      render={({ field }) => (
                        <FormInput
                          label="Name"
                          {...field}
                        />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="damage_telephone"
                      render={({ field }) => (
                        <FormInput
                          label="Telephone"
                          {...field}
                        />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="damage_email"
                      render={({ field }) => (
                        <FormInput
                          label="Email"
                          {...field}
                        />
                      )}
                    />
                  </View>
                </CardContent>
              </Card>
              <Card className='mt-4 mb-4'>
                <CardHeader>
                  <CardTitle>Who damaged the {getDamageSectionTitle(selectedType)}?</CardTitle>
                </CardHeader>
                <CardContent>
                  <View className="gap-4">
                    <FormField
                      control={form.control}
                      name="crew_id"
                      render={({ field }) => (
                        <Select onValueChange={(option) => field.onChange(option?.value || '')}>
                          <SelectTrigger className='w-[250px]'>
                            <SelectValue
                              className='text-foreground text-sm native:text-lg'
                              placeholder='Select a person'
                            />
                          </SelectTrigger>
                          <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                            <SelectGroup>
                              {crewList?.map((crewItem, index) => (
                                <SelectItem key={index} label={crewItem.crew?.name || ''} value={crewItem.crew_id?.toString() || ''}>
                                  {crewItem.crew?.name || ''}
                                </SelectItem>
                              ))}
                              <SelectItem label="Other" value="Other">
                                Other
                              </SelectItem>
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {selectedCrewId === 'Other' && (
                      <View className="gap-4">

                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormInput
                              label="Name"
                              {...field}
                            />
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="telephone"
                          render={({ field }) => (
                            <FormInput
                              label="Telephone"
                              {...field}
                            />
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormInput
                              label="Email"
                              {...field}
                            />
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="position"
                          render={({ field }) => (
                            <FormInput
                              label="Position"
                              {...field}
                            />
                          )}
                        />
                        <FormLabel>Address</FormLabel>
                        <FormField
                          control={form.control}
                          name="address"
                          render={({ field }) => (
                            <Textarea
                              {...field}
                              onChangeText={(value) => {
                                field.onChange(value);
                              }}
                              value={field.value || ''}
                            />
                          )}
                        />
                      </View>
                    )}
                  </View>
                </CardContent>
              </Card>
            </>
          )}
          {selectedType === 'Injury' && (
            <Card className='mt-4 mb-4'>
              <CardHeader>
                <CardTitle>Injury Type</CardTitle>
                <CardDescription className="mt-1">When completing an accident report, it's crucial to be thorough and accurate in describing the injuries. Please select from the following options.</CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="injuries"
                  render={({ field }) => (
                    <>
                      {INJURY_TYPE_SECTIONS.map(({ title, options }) => (
                        <View key={title} className="mb-4 border-b border-gray-300">
                          <Collapsible>
                            <CollapsibleTrigger className="py-3 flex flex-row items-center">
                              <Text className="text-base font-medium">{title}</Text>
                              <Ionicons name="chevron-down" size={16} className="ml-auto" />
                            </CollapsibleTrigger>
                            <CollapsibleContent className="pl-3 py-2">
                              {options.map(({ option, details: defaultDetails }) => {
                                const existing = field.value?.find((injury) => injury.type === option);

                                return (
                                  <View key={option} className="my-1">
                                    <View className="flex flex-row gap-2">
                                      <Checkbox
                                        checked={!!existing}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            // Add new injury
                                            field.onChange([
                                              ...(field.value || []),
                                              { type: option as InjuryType, details: "" }
                                            ]);
                                          } else {
                                            // Remove injury
                                            field.onChange(
                                              field.value?.filter((injury) => injury.type !== option)
                                            );
                                          }
                                        }}
                                      />
                                      <Text>{option}</Text>
                                    </View>

                                    {existing && (
                                      <View className="mt-1 mb-2">
                                        <Text className="mb-1">{defaultDetails}</Text>
                                        <Textarea
                                          value={existing.details}
                                          onChangeText={(text) => {
                                            const updated = field.value?.map((injury) =>
                                              injury.type === option
                                                ? { ...injury, details: text }
                                                : injury
                                            );
                                            field.onChange(updated);
                                          }}
                                        />
                                      </View>
                                    )}
                                  </View>
                                );
                              })}
                            </CollapsibleContent>
                          </Collapsible>
                        </View>
                      ))}
                      <FormMessage />
                    </>
                  )}
                />

              </CardContent>
            </Card>
          )}
          {selectedType === 'Injury' && (
            <Card className='mt-4 mb-4'>
              <CardHeader>
                <CardTitle>Injury Severity</CardTitle>
                <CardDescription>In addition to the location of the injury, indicate its severity. Select one</CardDescription>
                {INJURY_SEVERITIES.map(({ name, description }) => (
                  <View key={name} className="mt-2">
                    <Text className="font-semibold">{name}</Text>
                    <Text>{description}</Text>
                  </View>
                ))}
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="injury_severity"
                  render={({ field }) => (
                    <>
                      <Select onValueChange={(option) => field.onChange(option?.value || '')}>
                        <SelectTrigger className='w-[250px]'>
                          <SelectValue
                            className='text-foreground text-sm native:text-lg'
                            placeholder='Select injury severity'
                          />
                        </SelectTrigger>
                        <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                          <SelectGroup>
                            {INJURY_SEVERITIES.map(({ name }, index) => (
                              <SelectItem key={index} label={name} value={name}>
                                {name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </>
                  )}
                />
              </CardContent>
            </Card>
          )}
          {selectedType === 'Injury' && (
            <Card className='mt-4 mb-4'>
              <CardHeader>
                <CardTitle>Other relevant information</CardTitle>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="exposure"
                  render={({ field }) => (
                    <>
                      <Text className="font-semibold">Exposure</Text>
                      <Text>If the injury involved exposure to a hazardous substance, note the substance and duration of exposure.</Text>
                      <Textarea
                        numberOfLines={8}
                        {...field}
                        onChangeText={(value) => {
                          field.onChange(value);
                        }}
                        value={field.value || ''}
                      />
                      <FormMessage />
                    </>
                  )}
                />
                <FormField
                  control={form.control}
                  name="conciousness"
                  render={({ field }) => (
                    <>
                      <Text className="font-semibold mt-2">Conciousness</Text>
                      <Text>If there was a loss of consciousness, note the duration and cause.</Text>
                      <Textarea
                        numberOfLines={8}
                        {...field}
                        onChangeText={(value) => {
                          field.onChange(value);
                        }}
                        value={field.value || ''}
                      />
                      <FormMessage />
                    </>
                  )}
                />

                <FormField
                  control={form.control}
                  name="outdoors"
                  render={({ field }) => (
                    <>
                      <Text className="font-semibold mt-2">Environment</Text>
                      <Select onValueChange={(option) => field.onChange(option?.value === "outdoors")}>
                        <SelectTrigger className='w-[250px]'>
                          <SelectValue
                            className='text-foreground text-sm native:text-lg'
                            placeholder='Select indoors or outdoors'
                          />
                        </SelectTrigger>
                        <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                          <SelectGroup>
                            <SelectItem label={"Indoors"} value={"indoors"}>
                              Indoors
                            </SelectItem>
                            <SelectItem label={"Outdoors"} value={"outdoors"}>
                              Outdoors
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </>
                  )}
                />
              </CardContent>
            </Card>
          )}
          {selectedType === 'Vehicle Accident or Damage' && (
            <Card className='mt-4 mb-4'>
              <CardHeader>
                <CardTitle>What vehicle(s) were involved?</CardTitle>
              </CardHeader>
              <CardContent className="gap-3">
                {vehicleFields.map((vehicle, index) => (
                  <View key={vehicle.id} className="border p-4 rounded-md flex flex-col gap-2">
                    <View className="flex flex-row">
                      <FormField
                        control={form.control}
                        name={`vehicles.${index}.vehicle_id`}
                        render={({ field }) => (
                          <View>
                            <Select onValueChange={(option) => field.onChange(option?.value || "")}>
                              <SelectTrigger className='w-[250px] mb-2'>
                                <SelectValue
                                  className='text-foreground text-sm native:text-lg'
                                  placeholder='Select vehicle'
                                />
                              </SelectTrigger>
                              <SelectContent insets={contentInsets} className='w-[250px] z-[1000] bg-white shadow-lg'>
                                <SelectGroup>
                                  {vehiclesData?.data?.map((vehicle, index) => (
                                    <SelectItem key={index} label={`${vehicle.vehicle?.make} ${vehicle.vehicle?.model} (${vehicle.vehicle?.registration})`} value={vehicle.vehicle_id.toString() || ''}>
                                      {`${vehicle.vehicle?.make} ${vehicle.vehicle?.model} (${vehicle.vehicle?.registration})`}
                                    </SelectItem>
                                  ))}
                                  <SelectItem label="Other" value="Other">
                                    Other
                                  </SelectItem>
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </View>
                        )}
                      />
                      <Button
                        variant="ghost"
                        className="mb-2 mr-1 px-2"
                        onPress={() => removeVehicle(index)}
                      >
                        <Ionicons name="close" size={24} />
                      </Button>
                    </View>
                    {form.watch(`vehicles.${index}.vehicle_id`) === "Other" && (
                      <>
                        <FormField
                          control={form.control}
                          name={`vehicles.${index}.vehicle_reg`}
                          render={({ field }) => (
                            <FormInput
                              label="Number Plate"
                              {...field}
                            />
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`vehicles.${index}.vehicle_owner_name`}
                          render={({ field }) => (
                            <FormInput
                              label="Owner name"
                              {...field}
                            />
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`vehicles.${index}.vehicle_owner_telephone`}
                          render={({ field }) => (
                            <FormInput
                              label="Owner Telephone"
                              {...field}
                            />
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`vehicles.${index}.vehicle_owner_email`}
                          render={({ field }) => (
                            <FormInput
                              label="Owner Email"
                              {...field}
                            />
                          )}
                        />
                      </>
                    )}
                  </View>
                ))}
                <Button
                  className="mt-2"
                  variant="crew"
                  onPress={() =>
                    addVehicle({})
                  }
                >
                  Add Vehicle
                </Button>
              </CardContent>
            </Card>
          )}
          <Card className='mt-4 mb-4'>
            <CardHeader>
              <CardTitle>Please describe what happened</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="details"
                render={({ field }) => (
                  <>
                    <Textarea
                      numberOfLines={8}
                      {...field}
                      onChangeText={(value) => {
                        field.onChange(value);
                      }}
                      value={field.value || ''}
                    />
                    <FormMessage />
                  </>
                )}
              />
            </CardContent>
          </Card>
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Witnessed by</CardTitle>
            </CardHeader>
            <CardContent>
              <View className="gap-6">
                {witnessFields.map((fieldItem, index) => {
                  const selectedCrewId = form.watch(`witnesses.${index}.crew_id`);

                  return (
                    <View key={fieldItem.id} className="border p-4 rounded-md flex flex-col">
                      <View className="flex flex-row">
                        <FormField
                          control={form.control}
                          name={`witnesses.${index}.crew_id`}
                          render={({ field }) => (
                            <View className="flex flex-col">
                              <Select onValueChange={(value) => field.onChange(value?.value)}>
                                <SelectTrigger className="w-[260px]">
                                  <SelectValue
                                    className="text-foreground text-sm native:text-lg"
                                    placeholder="Select a person"
                                  />
                                </SelectTrigger>
                                <SelectContent insets={contentInsets} className="w-[260px] z-[1000] bg-white shadow-lg">
                                  <SelectGroup>
                                    {crewList?.map((crewItem, idx) => (
                                      <SelectItem
                                        key={idx}
                                        label={crewItem.crew?.name || ''}
                                        value={crewItem.crew_id?.toString() || ''}
                                      >
                                        {crewItem.crew?.name || ''}
                                      </SelectItem>
                                    ))}
                                    <SelectItem label="Other" value="Other">Other</SelectItem>
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </View>
                          )}
                        />

                        {witnessFields.length > 1 && (
                          <Button
                            variant="ghost"
                            className="mb-2 mr-1 px-2"
                            onPress={() => removeWitness(index)}
                          >
                            <Ionicons name="close" size={24} />
                          </Button>
                        )}
                      </View>

                      {selectedCrewId === 'Other' && (
                        <View className="gap-4 mt-4">
                          <FormField
                            control={form.control}
                            name={`witnesses.${index}.name`}
                            render={({ field }) => (
                              <FormInput label="Name" {...field} />
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`witnesses.${index}.telephone`}
                            render={({ field }) => (
                              <FormInput label="Telephone" {...field} />
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`witnesses.${index}.email`}
                            render={({ field }) => (
                              <FormInput label="Email" {...field} />
                            )}
                          />
                        </View>
                      )}
                    </View>
                  );
                })}

                <Button
                  className="mt-2"
                  variant="crew"
                  onPress={() =>
                    addWitness({ crew_id: '', name: '', telephone: '', email: '' })
                  }
                >
                  Add Witness
                </Button>
              </View>
            </CardContent>
          </Card>

          <Card className='mt-4'>
            <CardHeader>
              <CardTitle className="mb-4">Upload Photos *</CardTitle>
              <Text className="text-sm text-muted-foreground mb-4">At least one photo is required</Text>
              <FileInput onFileSelect={(file) => onFileSelect(file)} allowMultiple={true} />
              {photoError && (
                <Text className="text-sm text-red-500 mt-2">{photoError}</Text>
              )}
            </CardHeader>
            <CardContent>
            </CardContent>
          </Card>

          <Card className='mt-4 mb-20'>
            <CardHeader>
              <CardTitle>Important</CardTitle>
            </CardHeader>
            <CardContent>
              <Text>Please make sure you have filled in this accident report with as much detail as possible,
                when you have click the submit button below.
              </Text>
              <Button className='mt-4' size="default"
                variant="crew"
                onPress={form.handleSubmit(onSubmit, (error) => console.log(error))}
                disabled={isCreatingTask || uploadLoading}
              >
                Submit Accident Report
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </Form>
  )
}